/**
 * WhimsicalBox Portal Application
 * Orchestrates top-level state and links modular Material 3 components.
 */
const { useState, useEffect } = React;
const { ThemeProvider, CssBaseline, Box, Container, Typography } = MaterialUI;

// Modular Material 3 Components imported from window.Components
const {
  m3Theme,
  getM3Theme,
  applyM3CssVariables,
  resolveM3Mode,
  ThemePicker,
  Navbar,
  NavigationBar,
  getNavItems,
  landingTab,
  UserMenu,
  PortfolioHub,
  PagesDialog,
  Menu,
  MenuItem,
  GoogleIcon,
  AppsTab,
  AdminConsole,
  AccountPage,
  SignInFlow,
  AppSnackbar,
  LinearProgress,
  can,
  canAdminConsole
} = window.Components;

// The tab a URL hash opens: #/portfolio... -> 1 (About me), #/p/<page>... -> 'p:<page>', #/admin -> 2, #/account -> 3.
// Without one: the apps launchpad (0) when signed in, else the Home landing page (About me shows instead when there is no Home page).
function tabFromHash() {
  const h = window.location.hash || '';
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem('wb_user') || 'null'); } catch (e) {}
  if (h.startsWith('#/admin') && canAdminConsole(saved)) return 2;
  if (saved && !can(saved, 'portfolio')) return h.startsWith('#/account') ? 3 : 0; // no portfolio access (Guest preset): My Apps only
  if (h.startsWith('#/portfolio')) return 1;
  const m = h.match(/^#\/p\/([a-z0-9-]+)/);
  if (m) return `p:${m[1]}`;
  if (h.startsWith('#/account') && saved) return 3;
  return saved ? 0 : 'p:home';
}

// #/signin?next=<path>: the Sign In link of the other apps (SiteNav.js). Opens the sign-in dialog; once signed in, the
// visitor goes back to <path> (same-site paths only).
function signInFromHash() {
  const m = (window.location.hash || '').match(/^#\/signin(?:\?next=([^&]*))?/);
  if (!m) return null;
  let next = '';
  try { next = decodeURIComponent(m[1] || ''); } catch (e) { /* malformed: no return path */ }
  // A path on this site only: not //host, /\host, or with whitespace (browsers drop tabs/newlines, turning /\t/host into //host)
  return { next: /^\/(?![\/\\])[^\s\\]*$/.test(next) ? next : null };
}

// Its only props are the page key, so menus, dialogs and theme pickers opening in App do not re-render the whole portfolio
// (theme changes still reach it through the ThemeProvider context).
const PortfolioHubMemo = React.memo(PortfolioHub);

function App() {
  const signInRequest = React.useRef(signInFromHash());
  const [token, setToken] = useState(() => localStorage.getItem('wb_token') || '');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('wb_user');
    return saved ? JSON.parse(saved) : null;
  });
  // The tab survives a refresh via the URL hash (tabFromHash), and follows it on back / forward and on links such as the
  // Home page's portfolio previews.
  const [currentTab, setCurrentTab] = useState(tabFromHash);
  useEffect(() => {
    const onHash = () => setCurrentTab(prev => {
      const next = tabFromHash();
      if (next !== prev) window.scrollTo(0, 0);
      return next;
    });
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  // Without the portfolio permission an account only has My Apps, its account page and (if allowed) the Admin Console
  const appsOnly = !!user && !can(user, 'portfolio');
  const consoleOk = canAdminConsole(user);
  useEffect(() => {
    if (currentTab === 2 && !consoleOk) setCurrentTab(user ? 0 : landingTab([]));
    else if (appsOnly && currentTab !== 0 && currentTab !== 2 && currentTab !== 3) setCurrentTab(0);
  }, [appsOnly, consoleOk, currentTab]);
  useEffect(() => {
    const h = window.location.hash || '';
    if (currentTab === 1) {
      if (!h.startsWith('#/portfolio')) window.location.hash = '#/portfolio';
    } else if (currentTab === 'p:home' && !user) {
      // Guests have Home at the bare address
      if (h && !h.startsWith('#/p/home')) history.pushState(null, '', window.location.pathname + window.location.search);
    } else if (typeof currentTab === 'string') {
      if (!h.startsWith(`#/p/${currentTab.slice(2)}`)) window.location.hash = `#/p/${currentTab.slice(2)}`;
    } else if (currentTab === 3) {
      if (!h.startsWith('#/account')) window.location.hash = '#/account';
    } else if (currentTab !== 2 && (h.startsWith('#/portfolio') || h.startsWith('#/admin') || h.startsWith('#/p/') || h.startsWith('#/account'))) {
      // (not on the Admin Console: its #/admin/<section> must survive a refresh)
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (currentTab === 2 && consoleOk && !h.startsWith('#/admin')) {
      history.replaceState(null, '', window.location.pathname + window.location.search + '#/admin');
    }
  }, [currentTab, user]);

  // Material 3 Dynamic Theming States with localStorage persistence
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('wb_theme_mode') || localStorage.getItem('wb_theme') || 'dark';
  });

  const [themeSeed, setThemeSeed] = useState(() => {
    return localStorage.getItem('wb_theme_seed') || 'purple';
  });

  const [customThemeColor, setCustomThemeColor] = useState(() => {
    return localStorage.getItem('wb_theme_custom_color') || '#6750a4';
  });

  const [themePickerAnchor, setThemePickerAnchor] = useState(null);

  // Media query listener for OS-level dark/light switching when in 'auto' mode
  const [systemDark, setSystemDark] = useState(() => {
    return typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : true;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else if (mq.addListener) {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
  }, []);

  const effectiveMode = React.useMemo(() => {
    if (typeof resolveM3Mode === 'function') {
      return resolveM3Mode(themeMode);
    }
    if (themeMode === 'auto') return systemDark ? 'dark' : 'light';
    return themeMode === 'light' ? 'light' : 'dark';
  }, [themeMode, systemDark]);

  // Accessibility preferences (M3Core.setA11y): a change of contrast regenerates the colour scheme, so the theme is rebuilt
  const [a11yTick, setA11yTick] = useState(0);
  useEffect(() => {
    const on = () => setA11yTick(n => n + 1);
    window.addEventListener('wb-a11y', on);
    return () => window.removeEventListener('wb-a11y', on);
  }, []);
  const activeTheme = React.useMemo(() => {
    return typeof getM3Theme === 'function' ? getM3Theme(effectiveMode, themeSeed) : m3Theme;
  }, [effectiveMode, themeSeed, a11yTick]);

  useEffect(() => {
    if (activeTheme && activeTheme.palette && activeTheme.palette.m3) {
      if (typeof applyM3CssVariables === 'function') {
        applyM3CssVariables(activeTheme.palette.m3, effectiveMode);
      }
    }
  }, [activeTheme, effectiveMode]);

  const handleToggleTheme = () => {
    const nextMode = effectiveMode === 'dark' ? 'light' : 'dark';
    setThemeMode(nextMode);
    localStorage.setItem('wb_theme_mode', nextMode);
    localStorage.setItem('wb_theme', nextMode);
  };

  const handleSelectMode = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('wb_theme_mode', mode);
    localStorage.setItem('wb_theme', mode);
  };

  const handleSelectSeed = (seed) => {
    setThemeSeed(seed);
    localStorage.setItem('wb_theme_seed', seed);
  };

  const handleCustomThemeColorChange = (color) => {
    setCustomThemeColor(color);
    localStorage.setItem('wb_theme_custom_color', color);
  };

  const [apps, setApps] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [accountVisit, setAccountVisit] = useState(0); // remounts the account page (fresh form) each time it is opened

  // User Context Menu State
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const isUserMenuOpen = Boolean(userMenuAnchor);

  // Top bar pages (portfolio-pages.js): About me plus pages created by the admin, some inside dropdowns.
  // Starts from the list the last visit saved for the same account (wb_nav_pages, below), so the top bar links are there in the first frame
  const [savedPages] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem('wb_nav_pages') || 'null');
      const saved = JSON.parse(localStorage.getItem('wb_user') || 'null');
      return c && Array.isArray(c.pages) && c.uid === (localStorage.getItem('wb_token') && saved ? saved.id : null) ? c.pages : null;
    } catch (e) { return null; }
  });
  const [pages, setPages] = useState(savedPages || []);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [navMenu, setNavMenu] = useState(null); // { item, anchor }
  const editOn = window.EditMode.use();
  const [pagesDialog, setPagesDialog] = useState(false);
  const loadPages = React.useCallback(async () => {
    try {
      const list = (await window.PortfolioApi.pages.list()).pages;
      setPages(list);
      // The other apps' copy of this top bar (SiteNav.js) starts from this list, so their links show without waiting
      const saved = JSON.parse(localStorage.getItem('wb_user') || 'null');
      localStorage.setItem('wb_nav_pages', JSON.stringify({ uid: localStorage.getItem('wb_token') && saved ? saved.id : null, pages: list }));
    } catch (e) { /* keep the previous list */ }
    setPagesLoaded(true);
  }, []);
  useEffect(() => { loadPages(); }, [loadPages, user && user.id, user && (user.permissions || []).join()]);
  const navItems = getNavItems(user, pages);
  const openNavMenu = (item, anchor) => setNavMenu({ item, anchor });
  const pageKey = typeof currentTab === 'string' ? currentTab.slice(2) : null;
  const activePage = pageKey && pages.find(p => p.key === pageKey);
  const homeMissing = currentTab === 'p:home' && pagesLoaded && !activePage; // Home deleted or hidden: About me stands in
  // A page that is gone (deleted or hidden while it was open, or an old link) sends the visitor to the landing page instead of an error.
  // The list is fetched once more first, so a page created a moment ago is not mistaken for a missing one.
  const unknownPage = typeof currentTab === 'string' && pagesLoaded && !activePage && !homeMissing;
  useEffect(() => {
    if (!unknownPage) return undefined;
    let live = true;
    window.PortfolioApi.pages.list().then(r => {
      if (!live) return;
      if (r.pages.some(p => p.key === pageKey)) { setPages(r.pages); return; }
      history.replaceState(null, '', window.location.pathname + window.location.search);
      setCurrentTab(landingTab(r.pages));
    }).catch(() => {});
    return () => { live = false; };
  }, [unknownPage, pageKey]);

  const [authOpen, setAuthOpen] = useState(() => !!signInRequest.current && !token);
  useEffect(() => {
    const req = signInRequest.current;
    if (!req) return;
    if (window.location.hash.startsWith('#/signin')) history.replaceState(null, '', window.location.pathname + window.location.search);
    if (token && req.next) window.location.replace(req.next); // already signed in: straight back to the app
  }, []);

  // Session check and the apps catalog (signed-in users)
  useEffect(() => {
    if (!token) { setApps([]); setPageLoading(false); return; }
    const call = window.PortalApi.call;
    const me = fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => {
        // Expired or invalid token (e.g. server secret rotated): sign out instead of retrying forever
        if (res.status === 401 || res.status === 403) { handleLogout(); return {}; }
        return res.json();
      })
      .then(data => { if (data.user) updateUser(data.user); })
      .catch(console.error);
    const appsReq = call(token, 'GET', '/api/apps').then(data => setApps(data.apps || [])).catch(console.error);
    Promise.allSettled([me, appsReq]).finally(() => setTimeout(() => setPageLoading(false), 300));
  }, [token]);

  // A saved user (and, after a password change, its reissued token) from the account page or /api/auth/me
  const updateUser = (u, newToken) => {
    setUser(u);
    localStorage.setItem('wb_user', JSON.stringify(u));
    if (newToken) { setToken(newToken); localStorage.setItem('wb_token', newToken); }
  };

  const finishLogin = (data) => {
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('wb_token', data.token);
    localStorage.setItem('wb_user', JSON.stringify(data.user));
    setAuthOpen(false);
    const back = signInRequest.current && signInRequest.current.next;
    if (back) { window.location.href = back; return; }
    setCurrentTab(0);
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    setToken('');
    setUser(null);
    localStorage.removeItem('wb_token');
    localStorage.removeItem('wb_user');
    setApps([]);
    setCurrentTab('p:home');
  };

  const handleOpenProfile = () => {
    if (!user) return;
    setUserMenuAnchor(null);
    setAccountVisit(n => n + 1);
    setCurrentTab(3);
    window.scrollTo(0, 0);
  };

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'clip' }}>
        
        {/* Material 3 App Bar */}
        {/* Skip link: the first stop for keyboard and screen-reader users (the address bar is a hash router, so it must not change the hash) */}
        <a className="wb-skip" href="#main-content" onClick={e => { e.preventDefault(); const m = document.getElementById('main-content'); if (m) { m.focus({ preventScroll: true }); m.scrollIntoView({ block: 'start' }); } }}>Skip to main content</a>
        <Navbar
          user={user}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          onOpenAuth={() => setAuthOpen(true)}
          onUserMenuOpen={e => setUserMenuAnchor(e.currentTarget)}
          isUserMenuOpen={isUserMenuOpen}
          themeMode={themeMode}
          themeSeed={themeSeed}
          pages={pages}
          pagesReady={pagesLoaded || !!savedPages}
          onOpenNavMenu={openNavMenu}
          onManagePages={editOn ? () => setPagesDialog(true) : undefined}
          onToggleTheme={handleToggleTheme}
          onOpenThemePicker={e => setThemePickerAnchor(e.currentTarget)}
        />

        {/* Compact-screen navigation (hidden from the md breakpoint up, where the top bar shows destinations) */}
        <NavigationBar
          items={navItems.map(i => (i.id === 0 || i.id === 2 ? i : { ...i, inMenu: true }))}
          value={user ? currentTab : (currentTab === 0 ? landingTab(pages) : currentTab)}
          onChange={(id, el, opts) => {
            const item = navItems.find(i => i.id === id);
            if (item && item.children && !(opts && opts.fromMenu)) { openNavMenu(item, el); return; }
            if (!user && id === 0) { setAuthOpen(true); return; }
            setCurrentTab(id);
          }}
        />

        {/* Top App Bar Page Loading & Refreshing Progress Indicator */}
        {pageLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: { xs: 60, md: 68 },
              left: 0,
              right: 0,
              zIndex: 1200,
              width: '100%',
              pointerEvents: 'none'
            }}
          >
            <LinearProgress sx={{ height: 3, borderRadius: 0 }} />
          </Box>
        )}

        {/* Dropdown of a top bar page that groups other pages: the page itself first, then the pages inside it */}
        <Menu anchorEl={navMenu && navMenu.anchor} open={!!navMenu} onClose={() => setNavMenu(null)} minWidth={220}>
          {navMenu && [{ ...navMenu.item, children: undefined }, ...navMenu.item.children].map(c => (
            <MenuItem key={c.id} selected={currentTab === c.id} headline={c.label}
              leadingIcon={<GoogleIcon name={c.icon} size={20} />}
              onClick={() => { setNavMenu(null); setCurrentTab(c.id); }} />
          ))}
        </Menu>
        {can(user, 'edit.pages') && pagesDialog && (
          <PagesDialog open pages={pages} onClose={() => setPagesDialog(false)} onChanged={loadPages} />
        )}

        {/* User Profile Context Menu */}
        <UserMenu
          anchorEl={userMenuAnchor}
          open={isUserMenuOpen}
          onClose={() => setUserMenuAnchor(null)}
          user={user}
          currentTab={currentTab}
          onSelectTab={setCurrentTab}
          onManagePages={editOn ? () => { setUserMenuAnchor(null); setPagesDialog(true); } : undefined}
          editMode={editOn}
          onToggleEditMode={v => window.EditMode.set(v)}
          onOpenProfile={handleOpenProfile}
          onOpenPrivacy={() => { setUserMenuAnchor(null); window.Consent.openSettings(); }}
          themeMode={themeMode}
          onSelectMode={handleSelectMode}
          onOpenThemePicker={() => { setThemePickerAnchor(userMenuAnchor); setUserMenuAnchor(null); }}
          onLogout={handleLogout}
        />

        {/* Material 3 Theme & Palette Picker Popover */}
        <ThemePicker
          anchorEl={themePickerAnchor}
          open={Boolean(themePickerAnchor)}
          onClose={() => setThemePickerAnchor(null)}
          themeMode={themeMode}
          onSelectMode={handleSelectMode}
          themeSeed={themeSeed}
          onSelectSeed={handleSelectSeed}
          customColor={customThemeColor}
          onCustomColorChange={handleCustomThemeColorChange}
        />

        {/* Main Content Area */}
        {/* At least a screen tall on phones, where the footer is in the page flow (SiteFooter.js): it then starts below the fold, so the
            content loading in (and the footer's own link groups) never pushes a visible footer down (layout shift, CLS) */}
        <Container component="main" id="main-content" tabIndex={-1} maxWidth="lg" sx={{ outline: 'none', pt: 4, pb: { xs: user ? 'calc(80px + env(safe-area-inset-bottom, 0px) + 24px)' : 4, md: 4 }, flexGrow: 1, minHeight: { xs: '100dvh', md: 0 } }}>
          
          {/* VIEW 0: APPS LAUNCHPAD (Logged-in default) */}
          {user && currentTab === 0 && (
            <window.Components.PageDoodle target="page:apps" title="Doodle for the apps page">
            <AppsTab user={user} token={token} apps={apps} onOpenProfile={handleOpenProfile} />
            </window.Components.PageDoodle>
          )}

          {/* VIEW 1: PORTFOLIO (Default for public visitors or when tab 1 is selected) */}
          {(((!user || currentTab === 1) && currentTab !== 2 && typeof currentTab !== 'string') || homeMissing) && (
            <PortfolioHubMemo key={user ? user.id : "guest"} page="about" />
          )}
          {typeof currentTab === 'string' && activePage && (
            <PortfolioHubMemo key={`${activePage.key}-${user ? user.id : 'guest'}`} page={activePage.key} />
          )}
          {/* An unknown page redirects to the landing page (effect above); nothing is drawn while it does */}

          {/* VIEW 2: ADMIN CONSOLE (its sections follow the account's permissions) */}
          {consoleOk && currentTab === 2 && (
            <window.Components.PageDoodle target="page:admin" title="Doodle for the admin console">
            <AdminConsole token={token} user={user} />
            </window.Components.PageDoodle>
          )}


          {/* VIEW 3: ACCOUNT (signed-in users) */}
          {user && currentTab === 3 && (
            <AccountPage
              key={accountVisit}
              user={user}
              token={token}
              onUserChange={updateUser}
              themeMode={themeMode}
              onSelectThemeMode={handleSelectMode}
              themeSeed={themeSeed}
              onSelectThemeSeed={handleSelectSeed}
            />
          )}
        </Container>

        {/* Sign-in dialog and its passkey step */}
        <SignInFlow open={authOpen} onClose={() => setAuthOpen(false)} onSignedIn={finishLogin} />

        <window.Components.ConsentBanner />

        <AppSnackbar />

        {/* Footer: groups of links and a text line, editable in edit mode (SiteFooter.js) */}
        <window.Components.SiteFooter user={user} />
        {/* Floating buttons: back to top and your own links / phone (SiteFabs.js) */}
        <window.Components.SiteFabs user={user} />
        {/* Accessibility settings: text size, contrast, narrow view, reduce motion (AccessibilityFab.js) */}
        <window.Components.AccessibilityFab user={user} />
      </Box>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
// As a transition, the first render is done in small slices that give the main thread back between them (one long task of the
// whole page blocked input: Total Blocking Time); the server-prerendered hero stays on screen until React commits.
React.startTransition(() => root.render(<App />));
